import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import {
  AcademicCapIcon,
  PlusIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Curriculum, CurriculumCourse, CurriculumLevel } from '../types';
import CurriculumVisualization from '../components/CurriculumVisualization';

export default function CurriculumDetail() {
  const { curriculumId } = useParams();
  const { currentUser } = useAuth();
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null);
  const [courses, setCourses] = useState<CurriculumCourse[]>([]);
  const [levels, setLevels] = useState<CurriculumLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showAddLevel, setShowAddLevel] = useState(false);
  const [newLevelName, setNewLevelName] = useState('');
  const [newCourse, setNewCourse] = useState<Partial<CurriculumCourse>>({
    title: '',
    description: '',
    duration: undefined,
  });

  useEffect(() => {
    if (!currentUser || !curriculumId) return;
    const fetch = async () => {
      const curSnap = await getDoc(doc(db, 'curricula', curriculumId));
      if (!curSnap.exists()) {
        setCurriculum(null);
        setLoading(false);
        return;
      }
      const curData = curSnap.data() as Curriculum;
      if (curData.uid !== currentUser.uid) {
        setCurriculum(null);
        setLoading(false);
        return;
      }
      setCurriculum(curData);

      const [coursesSnap, levelsSnap] = await Promise.all([
        getDocs(query(collection(db, 'curriculumCourses'), where('curriculumId', '==', curriculumId), where('uid', '==', currentUser.uid))),
        getDocs(query(collection(db, 'curriculumLevels'), where('curriculumId', '==', curriculumId), where('uid', '==', currentUser.uid))),
      ]);
      const coursesData = coursesSnap.docs.map(d => d.data() as CurriculumCourse).sort((a, b) => a.order - b.order);
      const levelsData = levelsSnap.docs.map(d => d.data() as CurriculumLevel).sort((a, b) => a.order - b.order);
      setCourses(coursesData);
      setLevels(levelsData);
      setLoading(false);
    };
    fetch();
  }, [currentUser, curriculumId]);

  const handleAddCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !curriculumId || !newCourse.title?.trim()) return;
    const now = new Date().toISOString();
    const nextOrder = courses.length;
    const course: CurriculumCourse = {
      id: `cc-${Date.now()}`,
      curriculumId,
      title: newCourse.title.trim(),
      description: newCourse.description?.trim() || '',
      order: nextOrder,
      duration: newCourse.duration ? Number(newCourse.duration) : undefined,
      uid: currentUser.uid,
      createdAt: now,
    };
    await setDoc(doc(db, 'curriculumCourses', course.id), course);
    setCourses(prev => [...prev, course].sort((a, b) => a.order - b.order));
    setShowAddCourse(false);
    setNewCourse({ title: '', description: '', duration: undefined });
  };

  const handleDeleteCourse = async (id: string) => {
    if (!window.confirm('Bu kursu silmek istediğinize emin misiniz?')) return;
    await deleteDoc(doc(db, 'curriculumCourses', id));
    setCourses(prev => prev.filter(c => c.id !== id));
  };

  const handleAddLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !curriculumId || !newLevelName.trim()) return;
    const newLevel: CurriculumLevel = {
      id: `cl-${Date.now()}`,
      curriculumId,
      name: newLevelName.trim(),
      order: levels.length,
      uid: currentUser.uid,
    };
    await setDoc(doc(db, 'curriculumLevels', newLevel.id), newLevel);
    setLevels(prev => [...prev, newLevel].sort((a, b) => a.order - b.order));
    setShowAddLevel(false);
    setNewLevelName('');
  };

  const handleDeleteLevel = async (id: string) => {
    if (!window.confirm('Bu seviyeyi silmek istediğinize emin misiniz? Atanmış kurslar seviye atamasını kaybeder.')) return;
    await deleteDoc(doc(db, 'curriculumLevels', id));
    setLevels(prev => prev.filter(l => l.id !== id));
  };

  if (!currentUser) return <div className="p-8">Yükleniyor...</div>;
  if (loading) return <div className="card animate-pulse h-48" />;
  if (!curriculum) return <div className="p-8 text-gray-500">Müfredat bulunamadı.</div>;

  return (
    <div className="space-y-6">
      <Link
        to="/app/curricula"
        className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        Müfredatlara dön
      </Link>

      <div className="card">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{curriculum.title}</h1>
            {curriculum.description && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">{curriculum.description}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {curriculum.department && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {curriculum.department}
                </span>
              )}
              {curriculum.targetAudience && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  {curriculum.targetAudience}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              title="Yakında: Atamalar"
            >
              <UserGroupIcon className="w-4 h-4 mr-1.5" />
              Atamalar
            </button>
            <button
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              title="Yakında: ROI & Ölçme"
            >
              <ChartBarIcon className="w-4 h-4 mr-1.5" />
              ROI & Ölçme
            </button>
          </div>
        </div>
      </div>

      {/* Seviyeler */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Squares2X2Icon className="w-5 h-5" />
            Seviyeler
          </h2>
          <button
            onClick={() => setShowAddLevel(true)}
            className="inline-flex items-center px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
          >
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Seviye Ekle
          </button>
        </div>
        {levels.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-4">
            {levels.map(l => (
              <div
                key={l.id}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200"
              >
                <span className="font-medium">{l.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteLevel(l.id)}
                  className="text-red-500 hover:text-red-600 p-0.5"
                  aria-label="Sil"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Temel, Orta, İleri gibi seviyeler ekleyin. Kurslara bu seviyelerden birini atayın.
          </p>
        )}
      </div>

      {/* Müfredat Görünümü */}
      {courses.length > 0 && (
        <CurriculumVisualization curriculumId={curriculumId!} courses={courses} levels={levels} />
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Kurslar</h2>
        <button
          onClick={() => setShowAddCourse(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Kurs Ekle
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="card text-center py-12">
          <AcademicCapIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Bu müfredatta henüz kurs yok. İlk kursu ekleyerek başlayın.
          </p>
          <button
            onClick={() => setShowAddCourse(true)}
            className="btn-primary"
          >
            Kurs Ekle
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {courses.map((course, idx) => (
            <div
              key={course.id}
              className="card flex items-center justify-between group hover:shadow-md transition-shadow"
            >
              <Link
                to={`/app/curricula/${curriculumId}/courses/${course.id}`}
                className="flex-1 min-w-0"
              >
                <div className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {course.description || 'Açıklama yok'}
                      {course.duration ? ` • ${course.duration} dk` : ''}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary-600 flex-shrink-0" />
                </div>
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteCourse(course.id);
                }}
                className="ml-4 text-sm text-red-600 hover:text-red-700 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Sil
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddCourse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Yeni Kurs
              </h2>
              <form onSubmit={handleAddCourse} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Başlık *
                  </label>
                  <input
                    type="text"
                    value={newCourse.title || ''}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    className="input"
                    placeholder="Kurs adı"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Açıklama
                  </label>
                  <textarea
                    value={newCourse.description || ''}
                    onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                    className="input min-h-[80px]"
                    placeholder="Kısa açıklama"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Süre (dakika)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={newCourse.duration ?? ''}
                    onChange={(e) =>
                      setNewCourse({
                        ...newCourse,
                        duration: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    className="input"
                    placeholder="Örn: 45"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddCourse(false)}
                    className="btn-secondary flex-1"
                  >
                    İptal
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Ekle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddLevel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Yeni Seviye
              </h2>
              <form onSubmit={handleAddLevel} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Seviye adı *
                  </label>
                  <input
                    type="text"
                    value={newLevelName}
                    onChange={e => setNewLevelName(e.target.value)}
                    className="input"
                    placeholder="Örn: Temel, Orta, İleri"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowAddLevel(false)} className="btn-secondary flex-1">
                    İptal
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Ekle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
